import React, { FC } from 'react';
import { toast } from 'react-toastify';
import { Badge, ButtonGroup, CardBody, CardFooter, CardHeader } from 'reactstrap';
import { FeeModel } from '../../../../shared/orders/fee';
import {
  ActionButton,
  Filter,
  FilterTable,
  PageHeader,
  UserStateComponent,
} from '../../../components';
import { Currency } from '../../../components/Currency';
import { DateTime } from '../../../components/Date';
import { useTranslation } from '../../../translations';
import { Fetcher, renderName, useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import { CreateFee } from './createFee';

const feeManagementFilters: Filter<FeeModel>[] = [
  {
    displayName: 'common.status',
    name: 'status',
    options: [
      {
        filter: (fee) => !fee.deletedAt && fee.order?.status === 'unpaid',
        name: 'common.unpaid',
        value: 'unpaid',
      },
      {
        filter: (fee) => !fee.deletedAt && fee.order?.status === 'paid',
        name: 'common.paid',
        value: 'paid',
      },
      {
        filter: (fee) => !!fee.deletedAt,
        name: 'common.deleted_archived',
        value: 'deleted',
      },
    ],
  },
];

export const FeeManagement: FC = () => {
  const { ts } = useTranslation();
  const fetcher = useFetcher(async () => {
    return await api.getAllFees();
  });

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <UserStateComponent>
      <PageHeader>{ts('fee_management')}</PageHeader>
      <FilterTable<FeeModel>
        data={fetcher.data}
        filters={feeManagementFilters}
        infoRenderer={(fee) => <InfoSection fee={fee} refresh={fetcher.refresh} />}
        renderer={(fee) => <FeeItem fee={fee} />}
      />
    </UserStateComponent>
  );
};

const InfoSection: FC<{ readonly fee?: FeeModel; readonly refresh: () => void }> = ({
  fee,
  refresh,
}) => {
  const { ts } = useTranslation();
  if (!fee) {
    return (
      <CardBody>
        {ts('select_a_refund_request_from')}
        <hr />
        <CreateFee
          onSuccess={() => {
            toast.success(ts('new_fee_has_been_created'));
            refresh();
          }}
        />
      </CardBody>
    );
  }

  return (
    <>
      <CardHeader>
        {ts('fee_feeid', fee.id)} &mdash; <Currency value={fee.amount} /> &nbsp;
        <FeeBadge fee={fee} />
      </CardHeader>
      <CardBody>
        <h6>{ts('created')}</h6>
        <p>
          <DateTime value={fee.createdAt} />
        </p>

        <h6>{ts('issuer')}</h6>
        <p>{renderName(fee.user)}</p>

        <h6>{ts('reason')}</h6>
        <p>{fee.reason}</p>
      </CardBody>
      {fee.order?.status === 'unpaid' && !fee.deletedAt && (
        <CardFooter>
          <ButtonGroup>
            <ActionButton
              action={async () => {
                await api.deleteFee(fee.id);
              }}
              color="danger"
              id="deleteFee"
              onFailure={captureError}
              onSuccess={() => {
                toast.success(ts('fee_has_been_deleted'));
                refresh();
              }}
            >
              {ts('delete_fee')}
            </ActionButton>
          </ButtonGroup>
        </CardFooter>
      )}
    </>
  );
};

const FeeItem: FC<{ readonly fee: FeeModel }> = ({ fee }) => {
  const { ts } = useTranslation();
  return (
    <div className="table-item">
      <p>
        {ts('fee_feeid', fee.id)} &mdash; <Currency value={fee.amount} /> &nbsp;
        <FeeBadge fee={fee} />
      </p>
      <p>{renderName(fee.user)}</p>
    </div>
  );
};

const FeeBadge: FC<{ readonly fee: FeeModel }> = ({ fee }) => {
  const { ts } = useTranslation();
  if (fee.deletedAt) {
    return <Badge color="secondary">{ts('common.deleted_archived')}</Badge>;
  }

  if (fee.order?.status === 'paid') {
    return <Badge color="success">{ts('common.paid')}</Badge>;
  }

  return <Badge color="danger">{ts('not_yet_paid')}</Badge>;
};
